/*
 * gift-recipient-form.js
 * --------------------------------------------------------------------
 * Custom element backing the Pink.Flowers gifting recipient form.
 *
 * Responsibilities:
 *   1. Inline validation on blur + on submit
 *   2. Live character count on the gift message
 *   3. Block form submission if any required field is invalid
 *   4. On valid submit, write form values to CART ATTRIBUTES on the
 *      order (NOT line item properties). Cart attributes end up on
 *      order.note_attributes / order.customAttributes, which downstream
 *      tools (Make.com, Zapier, Omnisend) can map directly without
 *      Liquid loops or array indexing.
 *
 * Architecture note: this form does NOT write line item properties.
 * Form inputs intentionally have no `name` or `form` attributes, so they
 * are not part of the product form's add-to-cart submission. All gift
 * recipient data lives at the cart/order attribute level only.
 *
 * Form versioning: every gift submission stamps `_gift_form_version`
 * onto cart attributes. Bump this when changing field names or shape so
 * downstream automations can branch by version.
 *
 * Note: delivery date is captured by Zapiet, not by this form, and lives
 * as a line item property (Zapiet writes it directly).
 */

(function () {
  'use strict';

  if (window.customElements.get('gift-recipient-form')) return;

  // Bump when changing field shape/keys. Read by downstream automations.
  var GIFT_FORM_VERSION = '1.0';

  // Minimal but realistic email format check
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Map from data-gift-field key → cart attribute key.
  // Cart attribute keys are prefixed with "Gift" to avoid clashing with
  // anything Zapiet, Hulk Subs, or other apps already write to the cart.
  // Whatever's listed here is what gets posted to /cart/update.js — these
  // are the ONLY place the gift data lives on the order.
  var CART_ATTR_MAP = {
    name: 'Gift recipient name',
    email: 'Gift recipient email',
    message: 'Gift message',
    sender: 'Gift sender name',
    send_on: 'Gift send on'
  };

  class GiftRecipientForm extends HTMLElement {
    constructor() {
      super();
      this.productForm = null;
      this.fields = {};
      this.boundHandleSubmit = this.handleSubmit.bind(this);
    }

    connectedCallback() {
      var formId = this.dataset.productFormId;
      this.productForm = formId ? document.getElementById(formId) : null;

      // Cache field references by data-gift-field attribute
      this.querySelectorAll('[data-gift-field]').forEach((el) => {
        this.fields[el.dataset.giftField] = el;
      });

      this.bindFieldEvents();
      this.bindCharCount();

      if (this.productForm) {
        // Capture phase so we run before the product-form custom element's
        // own submit handler that posts to /cart/add.js
        this.productForm.addEventListener('submit', this.boundHandleSubmit, true);
      }
    }

    disconnectedCallback() {
      if (this.productForm) {
        this.productForm.removeEventListener('submit', this.boundHandleSubmit, true);
      }
    }

    bindFieldEvents() {
      Object.keys(this.fields).forEach((key) => {
        var el = this.fields[key];
        el.addEventListener('blur', () => this.validateField(key));
        el.addEventListener('input', () => this.clearError(key));
      });
    }

    bindCharCount() {
      var msg = this.fields.message;
      var counter = this.querySelector('[data-gift-char-current]');
      if (!msg || !counter) return;
      var update = () => {
        counter.textContent = String(msg.value.length);
      };
      msg.addEventListener('input', update);
      update();
    }

    /* -------------------------- validation core -------------------------- */

    validateField(key) {
      var el = this.fields[key];
      if (!el) return true;

      var value = (el.value || '').trim();
      var error = null;

      switch (key) {
        case 'name':
          if (!value) error = 'Please enter the recipient’s name.';
          break;
        case 'email':
          if (!value) error = 'Please enter the recipient’s email address.';
          else if (!EMAIL_RE.test(value)) error = 'Please enter a valid email address.';
          break;
        case 'send_on':
          if (value && !this.isFutureDate(value)) {
            error = 'Send-on date must be in the future.';
          }
          break;
      }

      if (error) {
        this.showError(key, error);
        return false;
      }
      this.clearError(key);
      return true;
    }

    validateAll() {
      var keys = ['name', 'email'];
      if (this.fields.send_on) keys.push('send_on');
      var firstInvalid = null;
      keys.forEach((k) => {
        var ok = this.validateField(k);
        if (!ok && !firstInvalid) firstInvalid = k;
      });
      return firstInvalid;
    }

    /* ----------------------------- helpers ------------------------------- */

    isFutureDate(isoDate) {
      var today = new Date();
      var todayIso =
        today.getFullYear() +
        '-' +
        String(today.getMonth() + 1).padStart(2, '0') +
        '-' +
        String(today.getDate()).padStart(2, '0');
      return isoDate > todayIso;
    }

    showError(key, message) {
      var el = this.fields[key];
      if (!el) return;
      el.setAttribute('aria-invalid', 'true');
      var errorBox = this.querySelector('#' + el.getAttribute('aria-describedby'));
      if (errorBox) {
        var span = errorBox.querySelector('.error-message');
        if (span) span.textContent = message;
        errorBox.hidden = false;
      }
    }

    clearError(key) {
      var el = this.fields[key];
      if (!el) return;
      el.removeAttribute('aria-invalid');
      var errorBox = this.querySelector('#' + el.getAttribute('aria-describedby'));
      if (errorBox) errorBox.hidden = true;
    }

    /* ---------------------- cart attribute mirroring --------------------- */

    /*
     * Posts current form values to /cart/update.js as cart attributes.
     * Returns a Promise that resolves once Shopify has accepted the update.
     *
     * We do this BEFORE the form's add-to-cart submit, so by the time the
     * order is created, the cart attributes are set and they end up on
     * order.note_attributes — the order-level field that Make.com,
     * Zapier, and Omnisend Custom Events can map directly.
     */
    mirrorToCartAttributes() {
      var attrs = {};
      Object.keys(CART_ATTR_MAP).forEach((fieldKey) => {
        var el = this.fields[fieldKey];
        if (!el) return;
        var value = (el.value || '').trim();
        // Always write — empty values overwrite stale previous cart attrs
        attrs[CART_ATTR_MAP[fieldKey]] = value;
      });
      // Marker so downstream tools can branch on "is this a gift order"
      attrs['Is gift order'] = 'true';
      // Schema version stamp — bump GIFT_FORM_VERSION above when changing
      // field keys or shape so automations can handle multiple versions.
      attrs['_gift_form_version'] = GIFT_FORM_VERSION;

      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: attrs })
      }).catch(function (err) {
        // Non-fatal — log but don't block add-to-cart. The order will still
        // be created, just without the gift attributes; Pink.Flowers can
        // still see the order and contact the buyer manually if needed.
        console.warn('[gift-recipient-form] cart attribute update failed:', err);
      });
    }

    /* --------------------------- submit guard ---------------------------- */

    handleSubmit(event) {
      var firstInvalid = this.validateAll();
      if (firstInvalid) {
        event.preventDefault();
        event.stopPropagation();
        if (typeof event.stopImmediatePropagation === 'function') {
          event.stopImmediatePropagation();
        }
        var el = this.fields[firstInvalid];
        if (el) {
          window.requestAnimationFrame(() => {
            el.focus({ preventScroll: true });
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          });
        }
        return;
      }

      // Validation passed — mirror form values to cart attributes BEFORE
      // the add-to-cart request fires. We intercept the submit, write the
      // cart attributes, then re-trigger submit once that's done.
      //
      // The flag prevents an infinite loop when we re-fire submit below.
      if (this._submitting) return;
      this._submitting = true;

      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation();
      }

      var form = this.productForm;
      this.mirrorToCartAttributes().then(function () {
        // Cart attrs now set. Resubmit the form so the product-form custom
        // element's add-to-cart handler runs as normal.
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit();
        } else {
          form.submit();
        }
      });
    }
  }

  window.customElements.define('gift-recipient-form', GiftRecipientForm);
})();